import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ExternalLink, Flame, Mail, Phone, ScrollText, TrendingUp, Users } from 'lucide-react';
import { formatInr, formatSignedPercent } from '../../utils/gmpBoard';
import { EXCHANGE_ALLOTMENT_URL } from '../../utils/issueSubscription';

const Card = ({ id, headingId, icon: Icon, title, badge, children }) => (
  <section id={id} aria-labelledby={headingId} className="scroll-mt-24 bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
    <div className="flex items-center justify-between gap-2">
      <h2 id={headingId} className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
        <Icon className="w-4 h-4 text-[#008080]" aria-hidden="true" />
        {title}
      </h2>
      {badge && <span className="text-[10px] font-bold text-slate-400">{badge}</span>}
    </div>
    {children}
  </section>
);

const Line = ({ label, value, tone = 'text-slate-900' }) => (
  <div className="flex items-center justify-between gap-3 py-2 text-xs">
    <span className="text-slate-500">{label}</span>
    <span className={`font-bold text-right tabular-nums ${tone}`}>{value}</span>
  </div>
);

// gmp: GMP board row (utils/gmpBoard.js) for this issue, or null
export const GmpCard = ({ gmp, isSme }) => (
  <Card headingId="gmp-card-heading" icon={TrendingUp} title="GMP & Grey Market" badge="Live Tracker">
    {gmp?.hasGmp ? (
      <>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Current GMP</p>
            <p className="mt-0.5 text-xl font-extrabold text-emerald-700 tabular-nums">{formatInr(gmp.gmp, 2)}</p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-right">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Expected Listing</p>
            <p className="mt-0.5 text-xl font-extrabold text-slate-900 tabular-nums">{gmp.estListing !== null ? formatInr(gmp.estListing, 2) : '—'}</p>
            {gmp.gmpPercent !== null && (
              <p className={`text-[10px] font-bold ${gmp.gmpPercent >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {formatSignedPercent(gmp.gmpPercent)} estimated gain
              </p>
            )}
          </div>
        </div>
        <div className="mt-2 divide-y divide-slate-100">
          {gmp.gmpDayLow !== null && gmp.gmpDayHigh !== null && (
            <Line label="Today’s GMP range" value={`${formatInr(gmp.gmpDayLow)} – ${formatInr(gmp.gmpDayHigh)}`} />
          )}
          <Line
            label="Grey desk sentiment"
            value={
              <span className="inline-flex items-center gap-1">
                {Array.from({ length: gmp.fireRating }).map((_, index) => (
                  <Flame key={index} className="w-3.5 h-3.5 text-orange-500" aria-hidden="true" />
                ))}
                {gmp.sentiment}
              </span>
            }
          />
          {gmp.updatedOn && <Line label="Quote updated" value={gmp.updatedOn} tone="text-slate-600" />}
        </div>
        <Link
          to={gmp.slug ? `/ipo-gmp/${gmp.slug}` : isSme ? '/sme-ipo-gmp' : '/ipo-gmp'}
          className="mt-2 inline-flex text-xs font-bold text-[#008080] hover:underline"
        >
          Full GMP trend →
        </Link>
        <p className="mt-2 text-[10px] text-slate-400 leading-relaxed">Grey market premiums are unofficial and indicative only.</p>
      </>
    ) : (
      <p className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
        No grey market quote for this issue yet.
      </p>
    )}
  </Card>
);

const CALL_TONE = {
  subscribe: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  neutral: 'bg-slate-50 text-slate-700 border-slate-200',
  avoid: 'bg-rose-50 text-rose-700 border-rose-200',
};

// summary: utils/issueSubscription.js summarizeBrokerReviews
export const BrokerConsensusCard = ({ summary, isSme }) => {
  const { total = 0, counts = { subscribe: 0, neutral: 0, avoid: 0 }, reviews = [] } = summary || {};
  const pct = (value) => (total ? Math.round((value / total) * 100) : 0);
  return (
    <Card headingId="consensus-heading" icon={Users} title="Broker Consensus" badge={total ? `${total} review${total === 1 ? '' : 's'}` : null}>
      {total ? (
        <>
          <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-slate-100" aria-hidden="true">
            <div className="bg-emerald-500" style={{ width: `${pct(counts.subscribe)}%` }} />
            <div className="bg-slate-400" style={{ width: `${pct(counts.neutral)}%` }} />
            <div className="bg-rose-500" style={{ width: `${pct(counts.avoid)}%` }} />
          </div>
          <ul className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[
              ['subscribe', 'Subscribe'],
              ['neutral', 'Neutral'],
              ['avoid', 'Avoid'],
            ].map(([key, label]) => (
              <li key={key} className={`rounded-xl border py-2 ${CALL_TONE[key]}`}>
                <p className="text-lg font-extrabold tabular-nums">{counts[key]}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider">
                  {label} ({pct(counts[key])}%)
                </p>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Coverage:{' '}
            {reviews.map((review, index) => (
              <React.Fragment key={`${review.name}-${index}`}>
                {index > 0 && ', '}
                {review.url ? (
                  <a href={review.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-700 hover:text-[#008080]">
                    {review.name}
                  </a>
                ) : (
                  <span className="font-semibold text-slate-700">{review.name}</span>
                )}{' '}
                <span className="text-slate-400">({review.call})</span>
              </React.Fragment>
            ))}
          </p>
        </>
      ) : (
        <p className="mt-3 text-xs text-slate-500">
          No broker reviews for this issue yet.{' '}
          <Link to={isSme ? '/sme-ipo-reviews' : '/ipo-reviews'} className="font-bold text-[#008080] hover:underline">
            See all IPO reviews
          </Link>
        </p>
      )}
    </Card>
  );
};

// profile: utils/issueSubscription.js buildIssuerProfile (null when the issuer isn't in the catalog)
export const RegistrarCard = ({ profile, company }) => {
  const registrar = profile?.registrar;
  return (
    <Card id="registrar" headingId="registrar-heading" icon={ScrollText} title="Official Registrar">
      {registrar ? (
        <div className="mt-2 space-y-1.5 text-xs">
          <p className="font-extrabold text-slate-900">{registrar.name}</p>
          {registrar.phone && (
            <a href={`tel:${registrar.phone.replace(/[^\d+]/g, '')}`} className="flex items-center gap-1.5 text-slate-600 hover:text-[#008080]">
              <Phone className="w-3.5 h-3.5" aria-hidden="true" />
              {registrar.phone}
            </a>
          )}
          {registrar.email && (
            <a href={`mailto:${registrar.email}`} className="flex items-center gap-1.5 text-slate-600 hover:text-[#008080] break-all">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
              {registrar.email}
            </a>
          )}
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-500">The registrar will appear here once {company}’s offer document is analysed.</p>
      )}
      <a
        href={registrar?.portal || EXCHANGE_ALLOTMENT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#008080] hover:underline"
      >
        Check allotment on {registrar?.portal ? 'the registrar’s portal' : 'BSE'}
        <ExternalLink className="w-3 h-3" aria-hidden="true" />
      </a>

      {(profile?.registeredOffice || profile?.cin) && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
            <Building2 className="w-3.5 h-3.5" aria-hidden="true" />
            Company Registered Office
          </p>
          <p className="mt-1 text-xs font-bold text-slate-900">{company}</p>
          {profile.registeredOffice && <p className="mt-0.5 text-[11px] text-slate-500 leading-relaxed">{profile.registeredOffice}</p>}
          {profile.cin && <p className="mt-1 text-[10px] font-semibold text-slate-400">CIN: {profile.cin}</p>}
        </div>
      )}
    </Card>
  );
};
